import { GUIDED_EDIT_DEFAULT_TIMEZONE } from "@common/interfaces/adsManagerGuidedEdit";
import {
  scheduleInputToEventWall,
  scheduleInputToUtcIso,
} from "@common/utils/adsetSchedule";
import {
  validateScheduledBudgetWallsWithinUtcFlight,
  type WallTimedBudgetSegmentLike,
} from "./scheduledBudgetWallFlightValidation";
import { wireScheduleToEventWall } from "./eventWallTime";
import type { ScheduledBudgetAdDeliveryWindowUtc } from "./eventWallTime";
import {
  validateScheduledBudgetWallFormFields,
  type ScheduledBudgetPhaseTimelineImplicitFlightWalls,
} from "./scheduledBudgetPhaseTimelineValidation";

/** First scheduled-budget validation failure for a form (path + user-facing message). */
export type ScheduledBudgetFormFieldError = {
  /** When set, use as full RHF path (e.g. `schedule.start_at`). */
  fullPath?: string;
  /** Path segment under `budget` (e.g. `segments[0].time_start`). */
  pathSuffix?: string;
  message: string;
};

/** Schedule walls or UTC ISO bounds for the full ad delivery window (campaign run). */
export type ScheduledBudgetDeliveryWindowInput = {
  scheduleStartAt?: string;
  scheduleEndAt?: string;
  deliveryWindowStartIso?: string;
  deliveryWindowEndIso?: string;
};

export type { ScheduledBudgetPhaseTimelineImplicitFlightWalls };

export type ScheduledBudgetFormValidationParams = {
  segments: WallTimedBudgetSegmentLike[];
  eventTimezone: string | undefined;
  /**
   * Full ad delivery window (campaign run start → end). Create flow passes event-wall
   * `schedule.*`; edit/detail pass UTC ISO from `campaign.schedule`.
   */
  deliveryWindow?: ScheduledBudgetDeliveryWindowInput | null;
  /**
   * When true, missing UTC delivery-window bounds yields an error (create flow requires run dates).
   * Edit modal omits this — delivery-window check is skipped when bounds are unknown.
   */
  requireUtcDeliveryWindow?: boolean;
  missingUtcDeliveryWindow?: ScheduledBudgetFormFieldError;
};

/** Implicit phase-chain anchors from run schedule walls or UTC delivery-window bounds. */
export function scheduledBudgetTimelineImplicitWalls(
  eventTimezone: string | undefined,
  deliveryWindow: ScheduledBudgetDeliveryWindowInput | null | undefined
): ScheduledBudgetPhaseTimelineImplicitFlightWalls | null {
  const tz = (eventTimezone ?? "").trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;
  const scheduleStartAt = String(deliveryWindow?.scheduleStartAt ?? "").trim();
  const scheduleEndAt = String(deliveryWindow?.scheduleEndAt ?? "").trim();
  if (scheduleStartAt && scheduleEndAt) {
    return {
      firstPhaseStartWall: scheduleInputToEventWall(scheduleStartAt, tz),
      lastPhaseEndWall: scheduleInputToEventWall(scheduleEndAt, tz),
    };
  }
  const deliveryWindowStartIso = String(
    deliveryWindow?.deliveryWindowStartIso ?? ""
  ).trim();
  const deliveryWindowEndIso = String(
    deliveryWindow?.deliveryWindowEndIso ?? ""
  ).trim();
  if (deliveryWindowStartIso && deliveryWindowEndIso) {
    return {
      firstPhaseStartWall: wireScheduleToEventWall(deliveryWindowStartIso, tz),
      lastPhaseEndWall: wireScheduleToEventWall(deliveryWindowEndIso, tz),
    };
  }
  return null;
}

function resolveUtcDeliveryWindowBounds(
  eventTimezone: string,
  deliveryWindow: ScheduledBudgetDeliveryWindowInput | null | undefined
): ScheduledBudgetAdDeliveryWindowUtc | null {
  const startIsoFromInput = String(
    deliveryWindow?.deliveryWindowStartIso ?? ""
  ).trim();
  const endIsoFromInput = String(
    deliveryWindow?.deliveryWindowEndIso ?? ""
  ).trim();
  if (startIsoFromInput && endIsoFromInput) {
    return {
      deliveryWindowStartIso: startIsoFromInput,
      deliveryWindowEndIso: endIsoFromInput,
    };
  }

  const scheduleStartAt = String(deliveryWindow?.scheduleStartAt ?? "").trim();
  const scheduleEndAt = String(deliveryWindow?.scheduleEndAt ?? "").trim();
  if (!scheduleStartAt || !scheduleEndAt) return null;

  const deliveryWindowStartIso =
    scheduleInputToUtcIso(scheduleStartAt, eventTimezone) ?? "";
  const deliveryWindowEndIso =
    scheduleInputToUtcIso(scheduleEndAt, eventTimezone) ?? "";
  if (!deliveryWindowStartIso || !deliveryWindowEndIso) return null;
  return { deliveryWindowStartIso, deliveryWindowEndIso };
}

/**
 * Runs scheduled-budget checks for create-flow Yup and the Engage edit-budget modal.
 * Returns the first field error, or `null` when valid. Does not include create-only rules
 * (e.g. minimum average daily budget).
 */
export function validateScheduledBudgetForm(
  params: ScheduledBudgetFormValidationParams
): ScheduledBudgetFormFieldError | null {
  const {
    segments,
    deliveryWindow,
    requireUtcDeliveryWindow,
    missingUtcDeliveryWindow,
  } = params;
  const tz =
    (params.eventTimezone ?? "").trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;

  const wallError = validateScheduledBudgetWallFormFields(
    segments,
    tz,
    scheduledBudgetTimelineImplicitWalls(tz, deliveryWindow)
  );
  if (wallError) {
    return {
      pathSuffix: wallError.pathSuffix,
      message: wallError.message,
    };
  }

  const utcBounds = resolveUtcDeliveryWindowBounds(tz, deliveryWindow);
  if (!utcBounds) {
    if (requireUtcDeliveryWindow && missingUtcDeliveryWindow) {
      return missingUtcDeliveryWindow;
    }
    return null;
  }

  const deliveryWindowError = validateScheduledBudgetWallsWithinUtcFlight({
    segments,
    eventTimezone: tz,
    flightStartIso: utcBounds.deliveryWindowStartIso,
    flightEndIso: utcBounds.deliveryWindowEndIso,
  });
  if (deliveryWindowError) {
    return { pathSuffix: "segments", message: deliveryWindowError.message };
  }

  return null;
}
